/**
 * AppLayout — shell principal (visual Sphere).
 *
 * Sidebar + coluna com a área rolável. O topo (Header) fica DENTRO da área
 * rolável, como no desenho aprovado, para que o hero do Dashboard (a esfera)
 * possa subir por trás dele. O conteúdo (`<main>`) é um item flex com altura
 * definida, então telas "app" (Live Board, `height: 100%`) continuam preenchendo.
 * `scrollKey` força o scroll ao topo quando a tela/etapa muda.
 *
 * Cores: `themeCssVars()` deriva as variáveis `--sp-*` do tema atual (o CSS do
 * shell só usa variáveis). Fundo: NavaBackdrop (cena azul + grade no topo).
 * Navegação mobile (≤640 px): gaveta com Escape, clique fora e retorno de foco.
 */
import { useRef, useEffect, useState, useCallback } from 'react';
import { useIsMutating, useMutationState } from '@tanstack/react-query';
import { themeCssVars } from '@/theme/palette';
import { NavaBackdrop } from '@/components/ui/NavaBackdrop';
import { BlockingOverlay } from '@/components/ui/BlockingOverlay';
import { GlobalAiActivity } from '@/components/ui/GlobalAiActivity';
import { useAgentsActivity } from '@/api/agents';
import { MotionProvider, useMotionPreference } from '@/ui/sphere/motion';
import { Sidebar } from './Sidebar';
import { Header } from './Header';
import '@/ui/sphere/sphere.css';

/** Preferência do menu lateral (pedido do dono, 2026-10-03: recolher para ganhar tela). */
export const SIDEBAR_KEY = 'nava.sidebar';

function lerSidebarCompacta() {
  try {
    return typeof localStorage !== 'undefined' && localStorage.getItem(SIDEBAR_KEY) === 'compacta';
  } catch {
    return false;
  }
}

function Shell({
  children, currentScreen, navigate, title, subtitle, scrollKey, hideTitle,
}) {
  const scrollRef = useRef(null);
  const sidebarRef = useRef(null);
  const menuButtonRef = useRef(null);
  const [navOpen, setNavOpen] = useState(false);
  // Menu lateral recolhido (só ícones) por escolha do usuário — lembrado neste navegador.
  const [navCompacta, setNavCompacta] = useState(lerSidebarCompacta);
  const alternarSidebar = useCallback(() => {
    setNavCompacta((v) => {
      const novo = !v;
      try { localStorage.setItem(SIDEBAR_KEY, novo ? 'compacta' : 'expandida'); } catch { /* só em memória */ }
      return novo;
    });
  }, []);
  const { enabled: motion } = useMotionPreference();

  // Conta as mutations de IA em andamento (marcadas com mutationKey: ['ai']).
  // Enquanto houver ≥ 1, o BlockingOverlay cobre toda a tela e trava a interação.
  const aiBusy = useIsMutating({ mutationKey: ['ai'] });
  // Mensagem contextual: lê o meta.aiLabel/aiSublabel da mutation de IA ativa
  // (cada hook declara o seu). Sem meta → o BlockingOverlay usa o texto default.
  const aiMetas = useMutationState({
    filters: { mutationKey: ['ai'], status: 'pending' },
    select: (m) => m.options?.meta,
  });
  const aiMeta = aiMetas.filter(Boolean).at(-1) || null;

  // ANDAMENTO NA ANIMAÇÃO: enquanto a IA trabalha, puxa o feed de atividade do tenant
  // e mostra a ÚLTIMA mensagem no overlay. Poll leve (2s) e SÓ quando há IA ativa.
  const activityQ = useAgentsActivity({
    windowHours: 1,
    enabled: aiBusy > 0,
    refetchInterval: aiBusy > 0 ? 2000 : false,
  });
  const _agents = activityQ.data?.agents || [];
  const _withMsg = _agents.filter((a) => a && String(a.message || '').trim());
  // Prefere um agente ATIVO; senão, a atividade mais recente com mensagem.
  const aiProgressLine = (_withMsg.find((a) => a.active) || _withMsg.at(-1))?.message || '';

  useEffect(() => {
    scrollRef.current?.scrollTo?.(0, 0);
    window.scrollTo?.(0, 0);
  }, [scrollKey]);

  const closeNav = useCallback((devolverFoco = true) => {
    setNavOpen(false);
    if (devolverFoco) menuButtonRef.current?.focus();
  }, []);

  // Gaveta aberta: foco no 1º item; Escape fecha e devolve o foco ao botão de menu.
  useEffect(() => {
    if (!navOpen) return undefined;
    sidebarRef.current?.querySelector('.sp-nav-item')?.focus();
    const onKey = (e) => { if (e.key === 'Escape') closeNav(true); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [navOpen, closeNav]);

  return (
    <div
      className="sp-app"
      data-nav={navOpen ? 'open' : 'closed'}
      data-sidebar={navCompacta ? 'compacta' : 'expandida'}
      data-motion={motion ? 'on' : 'off'}
      style={themeCssVars()}
    >
      <NavaBackdrop />

      {/* Overlay global: bloqueia a tela inteira enquanto a IA trabalha. */}
      <BlockingOverlay open={aiBusy > 0} label={aiMeta?.aiLabel} sublabel={aiMeta?.aiSublabel}
        progressLine={aiProgressLine} />

      {/* Indicador global NÃO-BLOQUEANTE de operações longas. */}
      <GlobalAiActivity />

      <a className="sp-skip" href="#main-content">Ir para o conteúdo</a>

      <div className="sp-app-body">
        <Sidebar
          ref={sidebarRef}
          currentScreen={currentScreen}
          navigate={navigate}
          onNavigate={() => navOpen && closeNav(false)}
          compacta={navCompacta}
          onAlternar={alternarSidebar}
        />
        {navOpen && (
          <button
            type="button"
            className="sp-nav-scrim"
            aria-label="Fechar menu"
            tabIndex={-1}
            onClick={() => closeNav(true)}
          />
        )}

        <div className="sp-app-col">
          <div ref={scrollRef} className="sp-app-main">
            <Header
              navigate={navigate}
              title={title}
              subtitle={subtitle}
              hideTitle={hideTitle}
              navOpen={navOpen}
              onToggleMenu={() => (navOpen ? closeNav(true) : setNavOpen(true))}
              menuButtonRef={menuButtonRef}
            />
            <main id="main-content" className="sp-app-content" tabIndex={-1}>
              {children}
            </main>
          </div>
        </div>
      </div>
    </div>
  );
}

export function AppLayout(props) {
  return (
    <MotionProvider>
      <Shell {...props} />
    </MotionProvider>
  );
}
